import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../api';
import {
  BarChart3, Users, CheckCircle, AlertTriangle, Target,
  TrendingUp, Award, User, BookOpen, XCircle, Clock,
  ChevronRight, Download, Shield
} from 'lucide-react';

const SkillsMatrix = ({ user, setUser }) => {
  const [tab, setTab] = useState('matrix');
  const [overview, setOverview] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [deptSummary, setDeptSummary] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filterDept, setFilterDept] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [empProfile, setEmpProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const deptParam = filterDept !== 'all' ? `?department_id=${filterDept}` : '';
      const [ovRes, gapRes, deptRes, deptsRes] = await Promise.all([
        api.get(`/skills-matrix/overview${deptParam}`),
        api.get('/skills-matrix/gaps'),
        api.get('/skills-matrix/department-summary'),
        api.get('/departments'),
      ]);
      setOverview(ovRes.data);
      setGaps(gapRes.data);
      setDeptSummary(deptRes.data);
      setDepartments(Array.isArray(deptsRes.data) ? deptsRes.data : []);
    } catch (err) { console.error(err); toast.error('Failed to load skills data'); }
    finally { setLoading(false); }
  }, [filterDept]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openProfile = async (employeeId) => {
    setSelectedEmployee(employeeId);
    setProfileLoading(true);
    try {
      const res = await api.get(`/skills-matrix/employee/${employeeId}`);
      setEmpProfile(res.data);
    } catch { toast.error('Failed to load profile'); }
    finally { setProfileLoading(false); }
  };

  const getStatusColor = (status) => ({
    completed: 'bg-emerald-500',
    in_progress: 'bg-blue-400',
    assigned: 'bg-amber-400',
    failed: 'bg-red-400',
    not_assigned: 'bg-slate-200',
  }[status] || 'bg-slate-200');

  const getStatusLabel = (status) => ({
    completed: 'Completed',
    in_progress: 'In Progress',
    assigned: 'Assigned',
    failed: 'Failed',
    not_assigned: 'Not Assigned',
  }[status] || status);

  const getSeverityBadge = (severity) => ({
    critical: <Badge className="bg-red-100 text-red-700">Critical</Badge>,
    warning: <Badge className="bg-amber-100 text-amber-700">Warning</Badge>,
    minor: <Badge className="bg-blue-100 text-blue-700">Minor</Badge>,
  }[severity] || <Badge variant="outline">{severity}</Badge>);

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  const summary = overview?.summary || {};

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="skills-matrix-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Skills Matrix</h1>
            <p className="text-slate-500 text-sm">Training competency mapping and gap analysis</p>
          </div>
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-48" data-testid="dept-filter">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(d => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-indigo-400">
            <CardContent className="p-4 flex items-center gap-3">
              <Users size={22} className="text-indigo-500" />
              <div>
                <p className="text-xs text-slate-500">Employees</p>
                <p className="text-xl font-bold text-slate-800">{summary.total_employees || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-400">
            <CardContent className="p-4 flex items-center gap-3">
              <BookOpen size={22} className="text-emerald-500" />
              <div>
                <p className="text-xs text-slate-500">Active Courses</p>
                <p className="text-xl font-bold text-slate-800">{summary.total_courses || 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-400">
            <CardContent className="p-4 flex items-center gap-3">
              <TrendingUp size={22} className="text-amber-500" />
              <div>
                <p className="text-xs text-slate-500">Avg Completion</p>
                <p className="text-xl font-bold text-slate-800">{summary.avg_completion_rate || 0}%</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-400">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle size={22} className="text-red-500" />
              <div>
                <p className="text-xs text-slate-500">Skill Gaps</p>
                <p className="text-xl font-bold text-red-700">{gaps?.summary?.total_gaps || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="matrix" data-testid="tab-matrix">Competency Matrix</TabsTrigger>
            <TabsTrigger value="gaps" data-testid="tab-gaps">Gap Analysis</TabsTrigger>
            <TabsTrigger value="departments" data-testid="tab-departments">Departments</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses">Course Stats</TabsTrigger>
          </TabsList>

          {/* MATRIX TAB */}
          <TabsContent value="matrix">
            <Card>
              <CardContent className="p-0">
                {!overview?.matrix?.length ? (
                  <div className="py-16 text-center text-slate-400">
                    <BarChart3 size={40} className="mx-auto mb-3 text-slate-300" />
                    <p>No skill data available. Assign training courses to employees first.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="matrix-table">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="p-3 text-left font-medium text-slate-600 sticky left-0 bg-slate-50 min-w-[180px] z-10">Employee</th>
                          {(overview.courses || []).map(c => (
                            <th key={c.id} className="p-2 text-center font-medium text-slate-600 min-w-[80px]">
                              <div className="text-xs leading-tight" title={c.name}>
                                {c.name.length > 12 ? c.name.slice(0, 12) + '...' : c.name}
                              </div>
                              {c.mandatory && <span className="text-red-400 text-[10px]">M</span>}
                            </th>
                          ))}
                          <th className="p-3 text-center font-medium text-slate-600 min-w-[80px]">Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {overview.matrix.map(row => (
                          <tr key={row.employee_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openProfile(row.employee_id)}
                            data-testid={`matrix-row-${row.employee_id}`}>
                            <td className="p-3 sticky left-0 bg-white z-10">
                              <p className="font-medium text-slate-800 text-xs">{row.employee_name}</p>
                              <p className="text-[10px] text-slate-400">{row.department}</p>
                            </td>
                            {row.skills.map((skill, i) => (
                              <td key={i} className="p-2 text-center">
                                <div className="flex justify-center">
                                  <div
                                    className={`w-7 h-7 rounded-md ${getStatusColor(skill.status)} flex items-center justify-center`}
                                    title={`${getStatusLabel(skill.status)}${skill.progress ? ` (${skill.progress}%)` : ''}`}
                                  >
                                    {skill.status === 'completed' && <CheckCircle size={14} className="text-white" />}
                                    {skill.status === 'in_progress' && <span className="text-white text-[10px] font-bold">{skill.progress}%</span>}
                                    {skill.status === 'assigned' && <Clock size={12} className="text-white" />}
                                    {skill.status === 'failed' && <XCircle size={12} className="text-white" />}
                                  </div>
                                </div>
                              </td>
                            ))}
                            <td className="p-3 text-center">
                              <Badge className={`text-xs ${row.completion_rate >= 80 ? 'bg-emerald-100 text-emerald-700' : row.completion_rate >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                {row.completion_rate}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Legend */}
                <div className="flex items-center gap-4 p-3 border-t bg-slate-50 text-xs text-slate-500 flex-wrap">
                  <span className="font-medium">Legend:</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> Completed</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-400 inline-block" /> In Progress</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400 inline-block" /> Assigned</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 inline-block" /> Failed</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-200 inline-block" /> Not Assigned</span>
                  <span className="flex items-center gap-1 text-red-400 font-medium">M = Mandatory</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* GAP ANALYSIS TAB */}
          <TabsContent value="gaps">
            <div className="space-y-4">
              {/* Gap summary pills */}
              <div className="flex gap-3 flex-wrap">
                <Badge className="bg-red-100 text-red-700 px-3 py-1">Critical: {gaps?.summary?.critical || 0}</Badge>
                <Badge className="bg-amber-100 text-amber-700 px-3 py-1">Warning: {gaps?.summary?.warning || 0}</Badge>
                <Badge className="bg-blue-100 text-blue-700 px-3 py-1">Minor: {gaps?.summary?.minor || 0}</Badge>
              </div>

              {(!gaps?.gaps?.length) ? (
                <Card>
                  <CardContent className="py-16 text-center text-slate-400">
                    <Shield size={40} className="mx-auto mb-3 text-emerald-300" />
                    <p className="text-emerald-600 font-medium">No skill gaps detected!</p>
                    <p className="text-sm">All mandatory courses have 100% completion.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {gaps.gaps.map((gap, i) => (
                    <Card key={i} className={`border-l-4 ${gap.gap_severity === 'critical' ? 'border-l-red-500' : gap.gap_severity === 'warning' ? 'border-l-amber-500' : 'border-l-blue-400'}`}
                      data-testid={`gap-card-${i}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-slate-800">{gap.course_name}</h3>
                            <p className="text-xs text-slate-500">{gap.department} &middot; {gap.category || 'General'}</p>
                          </div>
                          {getSeverityBadge(gap.gap_severity)}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-600 mb-2">
                          <span>Total: {gap.total_employees}</span>
                          <span className="text-emerald-600">Completed: {gap.completed}</span>
                          <span className="text-blue-600">In Progress: {gap.in_progress}</span>
                          <span className="text-slate-400">Not Assigned: {gap.not_assigned}</span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${gap.completion_pct >= 70 ? 'bg-blue-400' : gap.completion_pct >= 30 ? 'bg-amber-400' : 'bg-red-400'}`}
                            style={{ width: `${gap.completion_pct}%` }} />
                        </div>
                        <p className="text-right text-xs text-slate-500 mt-1">{gap.completion_pct}% complete</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* DEPARTMENTS TAB */}
          <TabsContent value="departments">
            {deptSummary.length === 0 ? (
              <Card><CardContent className="py-16 text-center text-slate-400">No department data available.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {deptSummary.map(dept => (
                  <Card key={dept.department_id} className="hover:shadow-md transition-shadow" data-testid={`dept-card-${dept.department_id}`}>
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-slate-800 mb-3">{dept.department}</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-slate-600">
                          <span>Employees</span><span className="font-medium">{dept.employees}</span>
                        </div>
                        <div className="flex justify-between text-slate-600">
                          <span>Assignments</span><span className="font-medium">{dept.total_assignments}</span>
                        </div>
                        <div className="flex justify-between text-emerald-700">
                          <span>Completed</span><span className="font-medium">{dept.completed}</span>
                        </div>
                        <div className="flex justify-between text-indigo-700">
                          <span>Certificates</span><span className="font-medium">{dept.certificates}</span>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Completion Rate</span>
                          <span className="font-medium">{dept.completion_rate}%</span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${dept.completion_rate >= 80 ? 'bg-emerald-500' : dept.completion_rate >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                            style={{ width: `${dept.completion_rate}%` }} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* COURSE STATS TAB */}
          <TabsContent value="courses">
            <Card>
              <CardContent className="p-0">
                {!overview?.course_stats?.length ? (
                  <div className="py-16 text-center text-slate-400">No course data available.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="course-stats-table">
                      <thead className="bg-slate-50 border-b text-left">
                        <tr>
                          <th className="p-3 font-medium text-slate-600">Course</th>
                          <th className="p-3 font-medium text-slate-600">Category</th>
                          <th className="p-3 font-medium text-slate-600 text-center">Type</th>
                          <th className="p-3 font-medium text-slate-600 text-center">Assigned</th>
                          <th className="p-3 font-medium text-slate-600 text-center">Completed</th>
                          <th className="p-3 font-medium text-slate-600 text-center">Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {overview.course_stats.map(cs => (
                          <tr key={cs.course_id} className="hover:bg-slate-50">
                            <td className="p-3 font-medium text-slate-800">{cs.course_name}</td>
                            <td className="p-3 text-slate-600">{cs.category || '-'}</td>
                            <td className="p-3 text-center">
                              {cs.mandatory ? <Badge className="bg-red-100 text-red-600 text-xs">Mandatory</Badge> : <Badge variant="outline" className="text-xs">Optional</Badge>}
                            </td>
                            <td className="p-3 text-center text-slate-700">{cs.assigned}</td>
                            <td className="p-3 text-center text-emerald-700 font-medium">{cs.completed}</td>
                            <td className="p-3 text-center">
                              <Badge className={`text-xs ${cs.completion_rate >= 80 ? 'bg-emerald-100 text-emerald-700' : cs.completion_rate >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                {cs.completion_rate}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Employee Profile Dialog */}
      {selectedEmployee && (
        <Dialog open={!!selectedEmployee} onOpenChange={() => { setSelectedEmployee(null); setEmpProfile(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User size={18} className="text-indigo-500" />
                {empProfile?.employee_name || 'Employee'} - Skill Profile
              </DialogTitle>
            </DialogHeader>
            {profileLoading ? (
              <div className="py-8 text-center"><div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto" /></div>
            ) : empProfile ? (
              <div className="space-y-4" data-testid="employee-skill-profile">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-slate-800">{empProfile.summary.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-emerald-700">{empProfile.summary.completed}</p>
                    <p className="text-xs text-emerald-600">Completed</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-blue-700">{empProfile.summary.in_progress}</p>
                    <p className="text-xs text-blue-600">In Progress</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-amber-700">{empProfile.summary.certificates}</p>
                    <p className="text-xs text-amber-600">Certificates</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Overall Completion:</span>
                  <Badge className={`${empProfile.summary.completion_rate >= 80 ? 'bg-emerald-100 text-emerald-700' : empProfile.summary.completion_rate >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                    {empProfile.summary.completion_rate}%
                  </Badge>
                </div>

                {/* Categories */}
                {Object.entries(empProfile.categories || {}).map(([cat, data]) => (
                  <Card key={cat}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Target size={14} className="text-indigo-500" />
                        {cat}
                        <Badge variant="outline" className="text-xs ml-auto">{data.completed}/{data.courses.length} completed</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {data.courses.map((c, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-50 text-sm">
                            <div className="flex items-center gap-2">
                              {c.status === 'completed' ? <CheckCircle size={14} className="text-emerald-500" /> : <Clock size={14} className="text-slate-400" />}
                              <span className="text-slate-800">{c.course_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {c.score != null && <span className="text-xs text-slate-500">{c.score}%</span>}
                              {c.certificate_id && (
                                <Badge className="bg-indigo-100 text-indigo-600 text-[10px]">
                                  <Award size={10} className="mr-0.5" />{c.certificate_id}
                                </Badge>
                              )}
                              <Badge className={`text-xs ${c.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : c.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                                {c.status === 'completed' ? 'Done' : c.status === 'in_progress' ? `${c.progress}%` : 'Pending'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-400 py-8">No data available</p>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
};

export default SkillsMatrix;
